import { useClientAuthStore } from '../store/clientAuthStore.js'

export interface ClientApiError extends Error {
  status: number
  body: unknown
}

const STATE_MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

async function request<T>(method: string, path: string, body?: unknown, customHeaders?: Record<string, string>): Promise<T> {
  const { accessToken, csrfToken } = useClientAuthStore.getState()
  const headers: Record<string, string> = customHeaders || { 'content-type': 'application/json' }
  if (accessToken) {
    headers.authorization = `Bearer ${accessToken}`
  }
  if (STATE_MUTATING.has(method) && csrfToken) {
    headers['x-csrf-token'] = csrfToken
  }

  const init: RequestInit = { method, headers, credentials: 'include' }
  if (body !== undefined) {
    if (body instanceof FormData) {
      init.body = body
      delete headers['content-type']
    } else {
      init.body = JSON.stringify(body)
    }
  }

  const res = await fetch(`/api${path}`, init)
  const text = await res.text()
  const trimmed = text.trim()
  const parsed: unknown = trimmed ? JSON.parse(trimmed) : null

  if (res.ok && parsed === null && res.status !== 204) {
    const err = new Error(`${method} ${path} → ${res.status} with an empty response body`) as ClientApiError
    err.status = res.status
    err.body = null
    throw err
  }

  if (!res.ok) {
    // A 401 only means "your session expired" when there was a session to
    // expire — for unauthenticated calls (login, register, forgot-password)
    // it's just a normal rejected request, and the caller already shows its
    // own error. Force-navigating here would blow away that error before
    // the user ever sees it.
    if (res.status === 401 && useClientAuthStore.getState().accessToken) {
      useClientAuthStore.getState().clearSession()
      window.location.href = '/client-portal/login'
    }

    let errorMessage = `${method} ${path} → ${res.status}`
    if (parsed && typeof parsed === 'object') {
      if ('message' in parsed && typeof parsed.message === 'string') {
        errorMessage = parsed.message
      } else if ('error' in parsed && typeof parsed.error === 'string') {
        errorMessage = parsed.error
      }
      if ('issues' in parsed && Array.isArray(parsed.issues)) {
        errorMessage += ': ' + (parsed.issues as Array<{ path?: unknown[]; message?: string }>)
          .map((i) => `${(i.path ?? []).join('.')}: ${i.message ?? ''}`)
          .join(', ')
      }
    }

    const err = new Error(errorMessage) as ClientApiError
    err.status = res.status
    err.body = parsed
    throw err
  }

  return parsed as T
}

async function downloadBlob(path: string): Promise<Blob> {
  const { accessToken } = useClientAuthStore.getState()
  const headers: Record<string, string> = {}
  if (accessToken) {
    headers.authorization = `Bearer ${accessToken}`
  }

  const res = await fetch(`/api${path}`, { headers, credentials: 'include' })
  if (!res.ok) {
    const err = new Error(`Download failed (${res.status})`) as ClientApiError
    err.status = res.status
    try {
      err.body = await res.text()
    } catch {
      err.body = null
    }
    throw err
  }
  return res.blob()
}

async function downloadAttachment(attachmentId: string): Promise<Blob> {
  return downloadBlob(`/client-portal/attachments/${attachmentId}`)
}

export const clientApi = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown, customHeaders?: Record<string, string>) => request<T>('POST', path, body, customHeaders),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
  downloadAttachment,
  downloadBlob,
}

export async function clientApiPostStream(
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<Response> {
  const { accessToken, csrfToken } = useClientAuthStore.getState()
  const headers: Record<string, string> = { 'content-type': 'application/json' }
  if (accessToken) {
    headers.authorization = `Bearer ${accessToken}`
  }
  if (csrfToken) {
    headers['x-csrf-token'] = csrfToken
  }

  const init: RequestInit = { method: 'POST', headers, credentials: 'include' }
  if (body !== undefined) init.body = JSON.stringify(body)
  if (signal) init.signal = signal

  const res = await fetch(`/api${path}`, init)

  if (!res.ok) {
    const text = await res.text().catch(() => '')
    let parsed: unknown = null
    try {
      parsed = text.trim() ? JSON.parse(text) : null
    } catch {
      // ignore
    }
    let message = `POST ${path} → ${res.status}`
    if (parsed && typeof parsed === 'object') {
      if ('message' in parsed && typeof parsed.message === 'string') message = parsed.message
      else if ('error' in parsed && typeof parsed.error === 'string') message = parsed.error
    } else if (text) {
      message = text.slice(0, 200)
    }
    const err = new Error(message) as ClientApiError
    err.status = res.status
    err.body = parsed ?? text
    throw err
  }

  return res
}
